/**
 * Style preset utilities.
 * Applies a named art style's tool preset to the canvas state
 * (tool, size, opacity, and optionally swaps the active colour
 * to one from the style's palette).
 */

import artStyles from '@/data/artStyles.json';

/**
 * Get a style definition by ID.
 */
export function getStyle(styleId) {
  return artStyles.styles.find(s => s.id === styleId) || null;
}

/**
 * Get all styles.
 */
export function getAllStyles() {
  return artStyles.styles;
}

/**
 * Get the workshop tradition data.
 */
export function getWorkshopTradition() {
  return artStyles.workshopTradition;
}

/**
 * Apply a style's tool preset to the canvas state.
 * Locks in tool, size, opacity, and restricts the palette to the style's colours.
 *
 * @param {function} dispatch - The AppContext dispatch function
 * @param {object} style - A style object from artStyles.json
 */
export function applyStylePreset(dispatch, style) {
  if (!style || !style.toolPreset) return;
  const { tool, size, opacity, palette } = style.toolPreset;

  if (tool) dispatch({ type: 'SET_TOOL', payload: tool });
  if (typeof size === 'number') dispatch({ type: 'SET_SIZE', payload: size });
  if (typeof opacity === 'number') dispatch({ type: 'SET_OPACITY', payload: opacity });

  // Set the first colour in the palette as the active colour
  if (palette && palette.length > 0) {
    dispatch({ type: 'SET_COLOUR', payload: palette[0] });
  }

  // Lock in the style — ColourPalette will show the restricted palette,
  // and the canvas will display a "Now drawing in X" indicator.
  dispatch({
    type: 'SET_ACTIVE_STYLE',
    payload: {
      id: style.id,
      name: style.name,
      emoji: style.emoji,
      colour: style.colour,
      palette: palette || [],
    },
  });
}

/**
 * Clear the active style and return to the full default palette.
 */
export function clearStylePreset(dispatch) {
  dispatch({ type: 'CLEAR_ACTIVE_STYLE' });
}

/**
 * Get a random challenge prompt from a style.
 */
export function pickRandomChallenge(style) {
  if (!style || !style.challengePrompts || style.challengePrompts.length === 0) return null;
  const prompts = style.challengePrompts;
  return prompts[Math.floor(Math.random() * prompts.length)];
}
